import { HeadProvider, Title } from 'react-head'
import { useTranslation } from 'react-i18next'

import 'swiper/swiper-bundle.css'

import { useEffect } from 'react'
import { Button, Table, Tooltip, useDrawer } from '@iveco-ti/iveco-ds'
import { GalleryHorizontalEnd, Pin } from 'lucide-react'
import { useQueryState } from 'nuqs'
import { Autoplay, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { GraphicArea } from '@/common/components/GraphicArea'
import { OrderByDropdown } from '@/common/components/OrderByDropdown'
import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import { DrawerCreateNews } from './components/DrawerCreateNews'
import { DrawerCreateNewsHighlighted } from './components/DrawerCreateNewsHighlighted'
import { DrawerEditNews } from './components/DrawerEditNews'
import { DrawerManageHighlightedNews } from './components/DrawerManageHighlightedNews'
import { DrawerPinnedNews } from './components/DrawerPinnedNews'
import { DrawerViewNews } from './components/DrawerViewNews'
import { DropdownButtonCreateNews } from './components/DropdownButtonCreateNews'
import { NewsCard } from './components/NewsCard'
import { NewsCardSkeleton } from './components/NewsCardSkeleton'
import { NewsHighlighted } from './components/NewsHighlighted'
import { useNews } from './hooks/useNews'
import { useNewsHighlighted } from './hooks/useNewsHighlighted'
import { useNewsShared } from './hooks/useNewsShared'
import { useViewNews } from './hooks/useViewNews'

export default function Panel() {
  const [newsId] = useQueryState('id', { defaultValue: '' })

  const { t } = useTranslation(['panel', 'common'])
  const {
    news,
    isLoadingNews,
    handleNewsSearch,
    handleChangeOrder,
    newsOrderBy,
    endElementRef,
    loadingMoreNews,
  } = useNews()
  const { newsHighlighted, isLoadingNewsHighlighted } = useNewsHighlighted()
  const drawerPinnedNews = useDrawer('drawer-pinned-news')
  const { newsShared } = useNewsShared(newsId)
  const { viewNews } = useViewNews()
  const { userInfo } = useGetUserInfo()
  const drawerManageHighlighted = useDrawer('drawer-manage-highlighted-news')

  const ORDER_BY_OPTIONS = [
    {
      label: t('news.search_bar.order_dropdown.fields.title.label'),
      field: 'title',
    },
    {
      label: t('news.search_bar.order_dropdown.fields.created_at.label'),
      field: 'createdAt',
    },
  ]

  useEffect(() => {
    if (!newsId || !newsShared) return

    viewNews(newsShared)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsId, newsShared])

  return (
    <>
      <HeadProvider>
        <Title>{t('news.browser.title')}</Title>
      </HeadProvider>

      <GraphicArea.Root className="p-0">
        <GraphicArea.Header className="p-0 m-0 mb-4">
          <Swiper
            autoplay={{
              delay: 5000,
              waitForTransition: false,
            }}
            pagination={{
              clickable: true,
            }}
            modules={[Autoplay, Pagination]}
            className="pb-5 w-full"
          >
            {newsHighlighted.map((post) => (
              <SwiperSlide key={post.id}>
                <NewsHighlighted post={post} />
              </SwiperSlide>
            ))}
          </Swiper>

          {isLoadingNewsHighlighted && (
            <div className="bg-neutral-200 h-52 w-full animate-pulse rounded-t-2xl" />
          )}

          <Table.Search
            searchName="search"
            searchPlaceholder={t(
              'common:authenticated_layout.search_input.placeholder',
            )}
            onSearch={handleNewsSearch}
          >
            <OrderByDropdown
              selected={newsOrderBy}
              onChangeOrder={handleChangeOrder}
              options={ORDER_BY_OPTIONS}
            />

            <Tooltip text={t('news.search_bar.pin_button.tooltip')}>
              <Button.Root variant="secondary" onClick={drawerPinnedNews.open}>
                <Button.Icon icon={Pin} />
              </Button.Root>
            </Tooltip>

            {userInfo?.UserPreference?.allowedPosts && (
              <Tooltip
                text={t('news.search_bar.manage_highlighted_button.tooltip')}
              >
                <Button.Root
                  variant="secondary"
                  onClick={drawerManageHighlighted.open}
                >
                  <Button.Icon icon={GalleryHorizontalEnd} />
                </Button.Root>
              </Tooltip>
            )}

            <DropdownButtonCreateNews />
          </Table.Search>
        </GraphicArea.Header>

        <GraphicArea.Body className="space-y-4 px-5 mb-2">
          {isLoadingNews && <NewsCardSkeleton />}

          {news.map((post) => (
            <NewsCard key={post.id} post={post} />
          ))}

          <div ref={endElementRef} />
          {loadingMoreNews && <NewsCardSkeleton />}
        </GraphicArea.Body>
      </GraphicArea.Root>

      <DrawerPinnedNews />
      <DrawerCreateNewsHighlighted />
      <DrawerCreateNews />
      <DrawerViewNews />
      <DrawerEditNews />
      <DrawerManageHighlightedNews />
    </>
  )
}
